import { useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { api, ApiError } from '../api';
import type { Enroll2faStartView, LoginResult } from '../api';
import { useAuth } from '../auth';
import { homePathForRole } from '../auth';
import { useT, LanguageSwitcher } from '../i18n';
import { TotpQr } from '../components';
import dict from './Login.i18n';

/**
 * Login + mandatory TOTP 2FA flow (DECISIONS.md).
 *
 * Steps:
 *  - 'credentials'  → email + password → POST /auth/login.
 *      → AUTHENTICATED       : store tokens, redirect home.
 *      → TOTP_REQUIRED       : go to 'totp'.
 *      → ENROLLMENT_REQUIRED : first login — go to 'enroll' (2FA is mandatory).
 *  - 'totp'         → 6-digit code → resubmit /auth/login with totpCode.
 *  - 'enroll'       → show QR/secret + recovery codes → confirm code → AUTHENTICATED.
 *
 * Card data is never handled here; this is staff/agent auth only.
 */

type Step = 'credentials' | 'set-password' | 'totp' | 'enroll';

/** Password policy (mirrors @jalupro/shared passwordSchema): ≥12, upper, lower, digit. */
function passwordProblemKey(pw: string): string | null {
  if (pw.length < 12) return 'pwTooShort';
  if (!/[a-z]/.test(pw)) return 'pwNoLower';
  if (!/[A-Z]/.test(pw)) return 'pwNoUpper';
  if (!/[0-9]/.test(pw)) return 'pwNoDigit';
  return null;
}

export default function LoginPage() {
  const t = useT(dict);
  const { isAuthenticated, role, login, completeLogin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [step, setStep] = useState<Step>('credentials');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [enrollmentToken, setEnrollmentToken] = useState('');
  const [enrollment, setEnrollment] = useState<Enroll2faStartView | null>(null);
  const [passwordChangeToken, setPasswordChangeToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Already signed in — bounce to the role home (or the page they came from).
  if (isAuthenticated && role) {
    const from = (location.state as { from?: Location } | null)?.from?.pathname;
    return <Navigate to={from ?? homePathForRole(role)} replace />;
  }

  async function finish(result: LoginResult) {
    if (result.status === 'AUTHENTICATED') {
      await completeLogin({ accessToken: result.accessToken, refreshToken: result.refreshToken });
      navigate(homePathForRole(role ?? 'AGENT'), { replace: true });
      return;
    }
    if (result.status === 'TOTP_REQUIRED') {
      setStep('totp');
      return;
    }
    if (result.status === 'PASSWORD_CHANGE_REQUIRED') {
      // Temp-password account — force the user to set their own first.
      setPasswordChangeToken(result.passwordChangeToken);
      setNewPassword('');
      setConfirmPassword('');
      setStep('set-password');
      return;
    }
    // ENROLLMENT_REQUIRED — kick off TOTP enrollment (mandatory on first login).
    setEnrollmentToken(result.enrollmentToken);
    const start = await api.auth.start2fa(result.enrollmentToken);
    setEnrollment(start);
    setStep('enroll');
  }

  async function submitSetPassword(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    const problem = passwordProblemKey(newPassword);
    if (problem) {
      setError(t(problem));
      return;
    }
    if (newPassword !== confirmPassword) {
      setError(t('pwMismatch'));
      return;
    }
    setBusy(true);
    try {
      await finish(
        await api.auth.setFirstLoginPassword({ passwordChangeToken, newPassword }),
      );
    } catch (err) {
      handle(err);
    } finally {
      setBusy(false);
    }
  }

  function handle(err: unknown) {
    setError(err instanceof ApiError ? err.message : t('genericError'));
  }

  async function submitCredentials(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await finish(await login({ email, password }));
    } catch (err) {
      handle(err);
    } finally {
      setBusy(false);
    }
  }

  async function submitTotp(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await finish(await login({ email, password, totpCode }));
    } catch (err) {
      handle(err);
    } finally {
      setBusy(false);
    }
  }

  async function submitEnrollment(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await finish(await api.auth.confirm2fa({ enrollmentToken, totpCode }));
    } catch (err) {
      handle(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="cl full row login-root" style={{ height: '100vh' }}>
      {/* Brand pane */}
      <div
        className="col login-brand"
        style={{
          width: '42%',
          background: 'var(--navy)',
          color: '#fff',
          padding: '56px 48px',
          justifyContent: 'space-between',
        }}
      >
        <div>
          <img src="/brand/lockup-on-navy.svg" alt="Jalupro Iberia" style={{ height: 24, width: 'auto', display: 'block' }} />
        </div>
        <div className="login-tagline">
          <div style={{ fontSize: 28, fontWeight: 300, lineHeight: 1.2 }}>
            {t('tagline')}
          </div>
          <div style={{ color: 'var(--side-text)', marginTop: 10, fontSize: 14 }}>
            {t('taglineSub')}
          </div>
        </div>
        <div className="login-agreement" style={{ color: 'var(--side-muted)', fontSize: 13 }}>
          {t('signinAgreement')}{' '}
          <Link to="/privacy" style={{ color: 'var(--side-text)', textDecoration: 'underline' }}>
            {t('privacyLink')}
          </Link>
          .
        </div>
      </div>

      {/* Auth pane */}
      <div className="col grow center login-auth" style={{ justifyContent: 'center', padding: 48 }}>
        {/* Language switcher — top-right of the auth pane */}
        <div style={{ position: 'absolute', top: 20, right: 24 }}>
          <LanguageSwitcher tone="light" />
        </div>

        <div style={{ width: 360, maxWidth: '100%' }}>
          {step === 'credentials' && (
            <form className="col gap-16" onSubmit={submitCredentials}>
              <div>
                <div className="h1">{t('credentialsHeading')}</div>
                <div className="meta mt-4">{t('credentialsHint')}</div>
              </div>
              <label className="col gap-4">
                <span className="label">{t('emailLabel')}</span>
                <input
                  className="input"
                  type="email"
                  autoComplete="username"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </label>
              <label className="col gap-4">
                <span className="label">{t('passwordLabel')}</span>
                <input
                  className="input"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </label>
              {error && <ErrorLine message={error} />}
              <button type="submit" className="btn primary" disabled={busy}>
                {busy ? t('signingIn') : t('signinBtn')}
              </button>
            </form>
          )}

          {step === 'set-password' && (
            <form className="col gap-16" onSubmit={submitSetPassword}>
              <div>
                <div className="h1">{t('setPwHeading')}</div>
                <div className="meta mt-4">{t('setPwHint')}</div>
              </div>
              <label className="col gap-4">
                <span className="label">{t('newPasswordLabel')}</span>
                <input
                  className="input"
                  type="password"
                  autoComplete="new-password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                />
                <span className="meta" style={{ fontSize: 12 }}>
                  {t('pwPolicyHint')}
                </span>
              </label>
              <label className="col gap-4">
                <span className="label">{t('confirmPasswordLabel')}</span>
                <input
                  className="input"
                  type="password"
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                />
              </label>
              {error && <ErrorLine message={error} />}
              <button type="submit" className="btn primary" disabled={busy}>
                {busy ? t('savingPw') : t('setPwBtn')}
              </button>
            </form>
          )}

          {step === 'totp' && (
            <form className="col gap-16" onSubmit={submitTotp}>
              <div>
                <div className="h1">{t('totpHeading')}</div>
                <div className="meta mt-4">{t('totpHint')}</div>
              </div>
              <TotpInput value={totpCode} onChange={setTotpCode} />
              {error && <ErrorLine message={error} />}
              <button type="submit" className="btn primary" disabled={busy || totpCode.length !== 6}>
                {busy ? t('verifying') : t('verifyBtn')}
              </button>
              <button
                type="button"
                className="btn"
                onClick={() => {
                  setStep('credentials');
                  setTotpCode('');
                  setError(null);
                }}
              >
                {t('backBtn')}
              </button>
            </form>
          )}

          {step === 'enroll' && enrollment && (
            <form className="col gap-16" onSubmit={submitEnrollment}>
              <div>
                <div className="h1">{t('enrollHeading')}</div>
                <div className="meta mt-4">
                  {t('enrollHint')}
                </div>
              </div>

              <div className="panel" style={{ padding: 14 }}>
                <div className="label">{t('scanQrLabel')}</div>
                <TotpQr otpauthUrl={enrollment.otpauthUrl} />
                <div className="meta mt-8" style={{ textAlign: 'center' }}>
                  {t('orEnterKeyManually')}
                </div>
                <div
                  className="mono"
                  style={{ wordBreak: 'break-all', fontSize: 13, marginTop: 4, textAlign: 'center' }}
                >
                  {enrollment.secret}
                </div>
              </div>

              <div className="panel" style={{ padding: 14, background: 'var(--amber-soft)' }}>
                <div className="label">{t('recoveryCodesLabel')}</div>
                <div className="mono" style={{ fontSize: 13, marginTop: 6, lineHeight: 1.7 }}>
                  {enrollment.recoveryCodes.map((c) => (
                    <div key={c}>{c}</div>
                  ))}
                </div>
              </div>

              <TotpInput value={totpCode} onChange={setTotpCode} />
              {error && <ErrorLine message={error} />}
              <button type="submit" className="btn primary" disabled={busy || totpCode.length !== 6}>
                {busy ? t('confirming') : t('confirmBtn')}
              </button>
            </form>
          )}

          <div className="meta mt-24" style={{ textAlign: 'center' }}>
            {t('readOur')}{' '}
            <Link to="/privacy" style={{ textDecoration: 'underline' }}>
              {t('privacyLinkDraft')}
            </Link>
            .
          </div>
        </div>
      </div>
    </div>
  );
}

function TotpInput({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const t = useT(dict);
  return (
    <label className="col gap-4">
      <span className="label">{t('sixDigitLabel')}</span>
      <input
        className="input mono"
        inputMode="numeric"
        autoComplete="one-time-code"
        maxLength={6}
        placeholder="000000"
        value={value}
        onChange={(e) => onChange(e.target.value.replace(/\D/g, '').slice(0, 6))}
        style={{ letterSpacing: '0.3em', fontSize: 16 }}
        required
      />
    </label>
  );
}

function ErrorLine({ message }: { message: string }) {
  return (
    <div
      className="panel"
      style={{ padding: '8px 12px', borderColor: 'var(--red)', background: 'var(--red-soft)' }}
    >
      <span style={{ color: 'var(--red)', fontSize: 13 }}>{message}</span>
    </div>
  );
}
